// frontend/src/App.js - полное обновление файла с учетом новых изменений
import { useEffect, useState } from 'react';
import { MobileNav } from "./components/Navigation";
import { DesktopLayout } from './components/layout/DesktopLayout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useSearchParams } from "react-router-dom";

import { AuthPage } from "./pages/AuthPage";
import { AthleteDashboard } from "./pages/athlete/Dashboard";
import { TrainingsPage } from "./pages/athlete/TrainingsPage";
import { SchedulePage } from "./pages/athlete/SchedulePage";
import { NutritionPage } from "./pages/athlete/NutritionPage";
import { EquipmentPage } from "./pages/athlete/EquipmentPage";
import { TournamentsPage } from "./pages/athlete/TournamentsPage";
import { CourtsPage } from "./pages/athlete/CourtsPage";
import { OpponentsPage } from "./pages/athlete/OpponentsPage";
import MatchmakingPage from "./pages/athlete/MatchmakingPage";
import { AchievementsPage } from "./pages/athlete/AchievementsPage";
import { NotificationSettingsPage } from "./pages/athlete/NotificationSettingsPage";
import { ProfilePage } from "./pages/athlete/ProfilePage";
import ProgressPage from "./pages/athlete/ProgressPage";
import MatchesPage from "./pages/athlete/MatchesPage";
import FitnessPage from "./pages/athlete/FitnessPage";
import FeedPage from "./pages/athlete/FeedPage";
import PostPublicPage from "./pages/PostPublicPage";
import ClubsPage from "./pages/athlete/ClubsPage";
import SubscriptionPage from "./pages/athlete/SubscriptionPage";
import TrainingPlannerPage from "./pages/athlete/TrainingPlannerPage";
import SubscriptionStatusPage from "./pages/athlete/SubscriptionStatusPage";
import EmailVerificationPage, { CheckEmailPage } from "./pages/EmailVerificationPage";
import PasswordResetPage from "./pages/PasswordResetPage";
import GlobalFeedPage from "./pages/athlete/GlobalFeedPage"; // Добавлен импорт

import { Toaster } from "./components/ui/sonner";

import { CoachDashboard } from "./pages/coach/CoachDashboard";
import CoachPlansPage from "./pages/coach/CoachPlansPage";
import CoachSessionsPage from "./pages/coach/CoachSessionsPage";

import AdminDashboard from "./pages/admin/AdminDashboard";

const getDefaultRouteForRole = (role) => {
  if (role === 'coach') return '/coach/dashboard';
  if (role === 'admin') return '/admin/dashboard';
  return '/dashboard';
};

const getDefaultFeedFallbackForRole = (role) => {
  if (role === 'coach') return '/coach/sessions';
  return '/schedule';
};

// Module-level role tuples for <ProtectedRoute allowedRoles={…}>.
// Extracting these (instead of inlining `={['athlete']}`) keeps prop identity
// stable across renders, avoiding spurious re-renders of ProtectedRoute and
// any context-driven children when AppRoutes re-renders.
const ROLES_ATHLETE = ['athlete'];
const ROLES_ATHLETE_COACH = ['athlete', 'coach'];
const ROLES_ATHLETE_COACH_ADMIN = ['athlete', 'coach', 'admin'];
const ROLES_COACH_ADMIN = ['coach', 'admin'];
const ROLES_ADMIN = ['admin'];


// =========================
// Protected Route
// =========================

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const { t } = useTranslation();

  // Show loading only on initial auth check
  if (loading && !user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-text-muted">{t('common.loading')}</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={getDefaultRouteForRole(user.role)} replace />;
  }

  return children;
};


// =========================
// Layout (исправленный)
// =========================

const Layout = ({ children }) => {
  const { user } = useAuth();

  if (!user) return children;

  return (
    <div>
      <DesktopLayout>{children}</DesktopLayout>
      <MobileNav />
    </div>
  );
};

const FeedEntryRedirect = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchEventId = searchParams.get('eventId');
  const rememberedEventId = typeof window !== 'undefined' ? window.localStorage.getItem('td:last-feed-event-id') : null;
  const targetEventId = searchEventId || rememberedEventId;
  const [manualEventId, setManualEventId] = useState('');

  if (targetEventId) {
    return <Navigate to={`/events/${targetEventId}/feed`} replace />;
  }

  const fallbackPath = getDefaultFeedFallbackForRole(user?.role);
  const normalizedEventId = manualEventId.trim();

  const handleOpenFeed = () => {
    if (!normalizedEventId) return;
    navigate(`/events/${normalizedEventId}/feed`);
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="feed-entry-resolver">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 max-w-2xl">
          <h1 className="text-xl font-semibold text-amber-900">
            {t('feed.eventRequired', 'Feed is available only for a selected event.')}
          </h1>
          <p className="text-amber-800 mt-2">
            {t('feed.entryResolverHint', 'Open an event first, then return to Feed.')}
          </p>
          <div className="mt-4 flex flex-col gap-3">
            <label className="text-sm text-amber-900 font-medium" htmlFor="feed-event-id-input">
              {t('feed.eventIdLabel', 'Event ID')}
            </label>
            <div className="flex gap-2">
              <input
                id="feed-event-id-input"
                data-testid="feed-event-id-input"
                value={manualEventId}
                onChange={(e) => setManualEventId(e.target.value)}
                placeholder={t('feed.eventIdPlaceholder', 'Enter event ID')}
                className="input-field bg-white"
              />
              <button
                type="button"
                data-testid="feed-open-event-button"
                onClick={handleOpenFeed}
                disabled={!normalizedEventId}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('feed.openEventFeed', 'Open event feed')}
              </button>
            </div>
            <Link to={fallbackPath} className="text-sm text-amber-900 underline">
              {t('feed.browseEventsFallback', 'Or browse your events list')}
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const PwaUxLayer = () => {
  const { t } = useTranslation();
  const [offline, setOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    const onUpdate = () => setUpdateAvailable(true);

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('sw-update-available', onUpdate);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('sw-update-available', onUpdate);
    };
  }, []);

  const handleUpdate = () => {
    const waitingWorker = window.__tdSwWaiting;
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
    window.location.reload();
  };

  if (!offline && !updateAvailable) return null;

  return (
    <div className="fixed top-0 inset-x-0 z-[80] px-3 pt-3 space-y-2">
      {offline && (
        <div className="bg-amber-100 border border-amber-300 text-amber-900 rounded-md px-3 py-2 text-sm flex items-center justify-between gap-3">
          <span>{t('pwa.offlineMode')}</span>
          <button onClick={() => window.location.reload()} className="text-xs font-semibold px-2 py-1 rounded bg-amber-200 hover:bg-amber-300">
            {t('pwa.retry')}
          </button>
        </div>
      )}
      {updateAvailable && (
        <div className="bg-blue-100 border border-blue-300 text-blue-900 rounded-md px-3 py-2 text-sm flex items-center justify-between gap-3">
          <span>{t('pwa.updateAvailable')}</span>
          <button onClick={handleUpdate} className="text-xs font-semibold px-2 py-1 rounded bg-blue-200 hover:bg-blue-300">
            {t('pwa.updateAction')}
          </button>
        </div>
      )}
    </div>
  );
};


// =========================
// Routes
// =========================

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>

      {/* Login */}
      <Route
        path="/login"
        element={
          user ? (
            <Navigate
              to={
                getDefaultRouteForRole(user.role)
              }
              replace
            />
          ) : (
            <AuthPage />
          )
        }
      />

      <Route path="/check-email" element={<CheckEmailPage />} />
      <Route path="/verify-email" element={<EmailVerificationPage />} />
      <Route path="/forgot-password" element={<PasswordResetPage />} />
      <Route path="/reset-password" element={<PasswordResetPage />} />

      {/* Athlete routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><AthleteDashboard /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/trainings"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><TrainingsPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/schedule"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><SchedulePage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/nutrition"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><NutritionPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/equipment"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><EquipmentPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/tournaments"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><TournamentsPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/courts"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><CourtsPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/opponents"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><OpponentsPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/matchmaking"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><MatchmakingPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/achievements"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><AchievementsPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/notifications"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><NotificationSettingsPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/progress"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><ProgressPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/matches"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><MatchesPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/fitness"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><FitnessPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Public post permalink — no auth required */}
      <Route path="/post/:postId" element={<PostPublicPage />} />
      <Route
        path="/share"
        element={
          user
            ? <Navigate to={user.role === 'athlete' ? '/schedule' : getDefaultRouteForRole(user.role)} replace />
            : <Navigate to="/login" replace />
        }
      />

      <Route
        path="/events/:eventId/feed"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE_COACH}>
            <Layout><FeedPage /></Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Новые маршруты для глобальной ленты */}
      <Route
        path="/feed/global"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE_COACH_ADMIN}>
            <Layout><GlobalFeedPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/feed"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE_COACH_ADMIN}>
            <Navigate to="/feed/global" replace />
          </ProtectedRoute>
        }
      />

      <Route
        path="/clubs"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><ClubsPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout><ProfilePage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <Layout><SubscriptionPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route path="/subscription/success" element={<SubscriptionStatusPage success />} />
      <Route path="/subscription/cancel" element={<SubscriptionStatusPage success={false} />} />

      <Route
        path="/training/planner"
        element={
          <ProtectedRoute allowedRoles={ROLES_ATHLETE}>
            <Layout><TrainingPlannerPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Coach */}
      <Route
        path="/coach/dashboard"
        element={
          <ProtectedRoute allowedRoles={ROLES_COACH_ADMIN}>
            <Layout><CoachDashboard /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/coach/plans"
        element={
          <ProtectedRoute allowedRoles={ROLES_COACH_ADMIN}>
            <Layout><CoachPlansPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/coach/sessions"
        element={
          <ProtectedRoute allowedRoles={ROLES_COACH_ADMIN}>
            <Layout><CoachSessionsPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN}>
            <Layout><AdminDashboard /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN}>
            <Layout><AdminDashboard /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/payments"
        element={
          <ProtectedRoute allowedRoles={ROLES_ADMIN}>
            <Layout><AdminDashboard /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Default */}
      <Route path="/" element={<Navigate to={user ? getDefaultRouteForRole(user.role) : '/login'} replace />} />
      <Route path="*" element={<Navigate to={user ? getDefaultRouteForRole(user.role) : '/login'} replace />} />

    </Routes>
  );
}


// =========================
// App Root
// =========================

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <PwaUxLayer />
          <AppRoutes />
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
